import { useState } from 'react';
import { waTemplates } from '../../../mocks/whatsapp';
import { bookingsData } from '../../../mocks/bookings';
import type { WaTemplate } from '../../../mocks/whatsapp';
import type { Booking } from '../../../mocks/bookings';

interface Props {
  initialTemplate?: WaTemplate | null;
  onClose: () => void;
  onSent: () => void;
}

type Step = 1 | 2 | 3;

export default function MessageComposer({ initialTemplate, onClose, onSent }: Props) {
  const [step, setStep] = useState<Step>(initialTemplate ? 2 : 1);
  const [selectedTemplate, setSelectedTemplate] = useState<WaTemplate | null>(initialTemplate || null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [customPhone, setCustomPhone] = useState('');
  const [customName, setCustomName] = useState('');
  const [useBooking, setUseBooking] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [bookingSearch, setBookingSearch] = useState('');

  const filteredBookings = bookingsData.filter(
    (b) =>
      b.customer.toLowerCase().includes(bookingSearch.toLowerCase()) ||
      b.id.toLowerCase().includes(bookingSearch.toLowerCase()) ||
      b.destination.toLowerCase().includes(bookingSearch.toLowerCase())
  );

  const recipientName = useBooking && selectedBooking ? selectedBooking.customer : customName;
  const recipientPhone = useBooking && selectedBooking ? selectedBooking.customerPhone : customPhone;

  const fillVariables = (text: string): string => {
    if (!selectedBooking) return text;
    return text
      .replace(/\{\{customer_name\}\}/g, selectedBooking.customer)
      .replace(/\{\{booking_ref\}\}/g, selectedBooking.id)
      .replace(/\{\{destination\}\}/g, selectedBooking.destination)
      .replace(/\{\{travel_date\}\}/g, new Date(selectedBooking.travelDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }))
      .replace(/\{\{pax\}\}/g, String(selectedBooking.pax))
      .replace(/\{\{total_amount\}\}/g, selectedBooking.totalAmount.toLocaleString())
      .replace(/\{\{balance_amount\}\}/g, (selectedBooking.totalAmount - selectedBooking.paidAmount).toLocaleString())
      .replace(/\{\{paid_amount\}\}/g, selectedBooking.paidAmount.toLocaleString())
      .replace(/\{\{payment_method\}\}/g, selectedBooking.payments[0]?.method || 'Bank Transfer')
      .replace(/\{\{transaction_ref\}\}/g, selectedBooking.payments[0]?.reference || 'N/A')
      .replace(/\{\{payment_date\}\}/g, selectedBooking.payments[0]?.date || 'N/A')
      .replace(/\{\{balance\}\}/g, (selectedBooking.totalAmount - selectedBooking.paidAmount).toLocaleString())
      .replace(/\{\{agent_name\}\}/g, selectedBooking.agent)
      .replace(/\{\{agent_phone\}\}/g, selectedBooking.customerPhone)
      .replace(/\{\{days_until\}\}/g, '3')
      .replace(/\{\{[^}]+\}\}/g, '–');
  };

  const handleSend = () => {
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSent(true);
      setTimeout(() => {
        onSent();
      }, 2000);
    }, 1800);
  };

  const canProceedStep1 = selectedTemplate !== null;
  const canProceedStep2 = useBooking ? selectedBooking !== null : (customName.trim() !== '' && customPhone.trim() !== '');

  const renderMessageBody = (text: string) => {
    return text.split('\n').map((line, i) => {
      const formatted = line.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
      return <p key={i} className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted || '&nbsp;' }} />;
    });
  };

  const categoryIcons: Record<string, string> = {
    Booking: 'ri-calendar-check-line',
    Payment: 'ri-money-dollar-circle-line',
    Reminder: 'ri-flight-takeoff-line',
    Visa: 'ri-passport-line',
    Custom: 'ri-star-smile-line',
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl" style={{ backgroundColor: '#25D36620' }}>
          <i className="ri-check-double-line" style={{ color: '#25D366' }} />
        </div>
        <div className="text-center">
          <p className="font-semibold text-gray-800 text-base">Message Sent!</p>
          <p className="text-sm text-gray-500 mt-1">WhatsApp message sent to {recipientName}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Step Indicator */}
      <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-100">
        {([1, 2, 3] as Step[]).map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                step === s
                  ? 'text-white'
                  : step > s
                  ? 'bg-emerald-500 text-white'
                  : 'bg-gray-100 text-gray-400'
              }`}
              style={step === s ? { backgroundColor: '#25D366' } : {}}
            >
              {step > s ? <i className="ri-check-line text-sm" /> : s}
            </div>
            <span className={`text-xs font-medium ${step === s ? 'text-gray-800' : 'text-gray-400'}`}>
              {s === 1 ? 'Template' : s === 2 ? 'Recipient' : 'Review & Send'}
            </span>
            {s < 3 && <i className="ri-arrow-right-s-line text-gray-300" />}
          </div>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* Step 1 — Choose Template */}
        {step === 1 && (
          <div>
            <p className="text-sm text-gray-500 mb-4">Choose a template to send:</p>
            <div className="space-y-2">
              {waTemplates
                .filter((t) => t.status === 'Active')
                .map((t) => (
                  <div
                    key={t.id}
                    onClick={() => setSelectedTemplate(t)}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      selectedTemplate?.id === t.id ? 'border-green-300 bg-green-50' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${t.color}18` }}
                    >
                      <i className={`${t.icon} text-base`} style={{ color: t.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{t.name}</p>
                      <p className="text-xs text-gray-400 truncate">{t.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-400">{t.usageCount} sent</span>
                      {selectedTemplate?.id === t.id && (
                        <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#25D366' }}>
                          <i className="ri-check-line text-white text-xs" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Step 2 — Choose Recipient */}
        {step === 2 && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => setUseBooking(true)}
                className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer ${useBooking ? 'text-white border-transparent' : 'text-gray-500 border-gray-200 bg-white'}`}
                style={useBooking ? { backgroundColor: '#25D366', borderColor: '#25D366' } : {}}
              >
                Link to Booking
              </button>
              <button
                onClick={() => setUseBooking(false)}
                className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer ${!useBooking ? 'text-white border-transparent' : 'text-gray-500 border-gray-200 bg-white'}`}
                style={!useBooking ? { backgroundColor: '#25D366', borderColor: '#25D366' } : {}}
              >
                Enter Manually
              </button>
            </div>

            {useBooking ? (
              <div>
                <input
                  type="text"
                  placeholder="Search bookings by name, ref, or destination..."
                  value={bookingSearch}
                  onChange={(e) => setBookingSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-3 outline-none focus:border-green-300"
                />
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {filteredBookings.map((b) => (
                    <div
                      key={b.id}
                      onClick={() => setSelectedBooking(b)}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                        selectedBooking?.id === b.id ? 'border-green-300 bg-green-50' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600 flex-shrink-0">
                        {b.customerAvatar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-800 truncate">{b.customer}</p>
                          <span className="text-xs text-gray-400 flex-shrink-0">{b.id}</span>
                        </div>
                        <p className="text-xs text-gray-400 truncate">{b.destination} · {b.customerPhone}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${b.status === 'Confirmed' ? 'bg-emerald-50 text-emerald-700' : b.status === 'Pending' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                        {b.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Recipient Name</label>
                  <input
                    type="text"
                    placeholder="e.g. John Smith"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-green-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">WhatsApp Number (with country code)</label>
                  <input
                    type="text"
                    placeholder="e.g. +971 50 123 4567"
                    value={customPhone}
                    onChange={(e) => setCustomPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-green-300"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3 — Review & Send */}
        {step === 3 && selectedTemplate && (
          <div>
            {/* To: */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl mb-4">
              <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
                {(recipientName || '?').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">{recipientName || '—'}</p>
                <p className="text-xs text-gray-400">{recipientPhone || '—'}</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
                <i className="ri-whatsapp-line" style={{ color: '#25D366' }} />
                WhatsApp
              </div>
            </div>

            {/* Template badge */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${categoryIcons[selectedTemplate.category] || 'ri-message-2-line'} text-sm`} style={{ color: selectedTemplate.color }} />
              </div>
              <span className="text-xs font-medium text-gray-600">{selectedTemplate.name}</span>
            </div>

            {/* WhatsApp Preview */}
            <div className="bg-[#e5ddd5] rounded-xl p-4" style={{ backgroundImage: 'radial-gradient(circle, #d4cbc3 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
              <div className="bg-white rounded-xl p-4 max-w-xs shadow-sm">
                {selectedTemplate.headerText && (
                  <p className="text-sm font-semibold text-gray-800 mb-2">{fillVariables(selectedTemplate.headerText)}</p>
                )}
                <div className="space-y-0.5">
                  {renderMessageBody(fillVariables(selectedTemplate.bodyText))}
                </div>
                {selectedTemplate.footerText && (
                  <p className="text-xs text-gray-400 mt-2">{selectedTemplate.footerText}</p>
                )}
                <p className="text-xs text-gray-400 text-right mt-2">9:14 AM ✓✓</p>
              </div>
              {selectedTemplate.buttons && (
                <div className="mt-2 space-y-1.5 max-w-xs">
                  {selectedTemplate.buttons.map((btn, i) => (
                    <div key={i} className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-white shadow-sm text-sm font-medium" style={{ color: '#25D366' }}>
                      <i className={`${btn.type === 'URL' ? 'ri-external-link-line' : btn.type === 'PHONE' ? 'ri-phone-line' : 'ri-chat-check-line'}`} />
                      {btn.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
        <button
          onClick={step === 1 ? onClose : () => setStep((s) => (s - 1) as Step)}
          className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
        >
          {step === 1 ? 'Cancel' : 'Back'}
        </button>

        {step < 3 ? (
          <button
            disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
            onClick={() => setStep((s) => (s + 1) as Step)}
            className="px-5 py-2 text-sm text-white rounded-lg font-medium cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
            style={{ backgroundColor: '#25D366' }}
          >
            Continue
          </button>
        ) : (
          <button
            onClick={handleSend}
            disabled={sending}
            className="flex items-center gap-2 px-5 py-2 text-sm text-white rounded-lg font-medium cursor-pointer whitespace-nowrap disabled:opacity-70 transition-opacity"
            style={{ backgroundColor: '#25D366' }}
          >
            {sending ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill" />
                Send via WhatsApp
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
